import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { 
  Loader2,
  Clock,
  Users,
  Sparkles,
  Check,
  Calendar,
  DollarSign,
  Leaf,
  Dumbbell,
  Zap,
  Wheat,
  Tag,
  ChevronRight
} from 'lucide-react';
import { haptic } from '@/utils/haptics';
import { trackPathUsed, trackPlanStarted, trackPlanCompleted } from '@/utils/analytics';

// Icon mapping for paths
const pathIcons = {
  'salad': Leaf,
  'users': Users,
  'dumbbell': Dumbbell,
  'piggy-bank': DollarSign,
  'leaf': Leaf,
  'zap': Zap,
  'wheat-off': Wheat,
  'tag': Tag,
};

/**
 * PathSelector - The "One Decision" Meal Planning Experience
 * Users pick a path → Get a full week of meals → Send to cart
 */
const PathSelector = ({ onComplete, compact = false }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPath, setSelectedPath] = useState(null);
  const [previewSheetOpen, setPreviewSheetOpen] = useState(false);
  const [previewMeals, setPreviewMeals] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Load paths
  useEffect(() => {
    const loadPaths = async () => {
      try {
        const response = await api.get('/paths');
        setPaths(response.data.paths || []);
      } catch (error) {
        console.error('Failed to load paths:', error);
        toast.error('Failed to load meal plans');
      } finally {
        setLoading(false);
      }
    };
    loadPaths();
  }, [api]);

  // Handle path selection
  const handlePathSelect = async (path) => {
    haptic('medium');
    setSelectedPath(path);
    setPreviewSheetOpen(true);
    setLoadingPreview(true);
    
    try {
      const response = await api.post(`/paths/${path.id}/preview`);
      setPreviewMeals(response.data.preview_meals || []);
    } catch (error) {
      console.error('Failed to load preview:', error);
      setPreviewMeals([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Generate meal plan from path
  const handleGenerate = async () => {
    if (!selectedPath) return;
    
    setGenerating(true);
    haptic('medium');
    
    // Track path usage and plan start
    trackPlanStarted(selectedPath.id, selectedPath.name);
    trackPathUsed(selectedPath.id, selectedPath.name);
    
    try {
      const response = await api.post(`/paths/${selectedPath.id}/generate`, {
        path_id: selectedPath.id,
        meals_count: selectedPath.mealsPerWeek || 5
      });
      
      haptic('success');
      toast.success(`${response.data.meals_generated} meals added to your week!`);
      setPreviewSheetOpen(false);
      
      // Track plan completed
      trackPlanCompleted(response.data.meals_generated, selectedPath.id);
      
      // Navigate to meal planner or call completion handler
      if (onComplete) {
        onComplete(response.data);
      } else {
        navigate('/meal-planner');
      }
    } catch (error) {
      console.error('Failed to generate meal plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate meals');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Compact mode for inline dashboard display
  if (compact) {
    return (
      <div data-testid="path-selector-compact">
        {/* Horizontal Scroll Path Cards */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
          {paths.slice(0, 6).map((path) => (
            <CompactPathCard
              key={path.id}
              path={path}
              onSelect={() => handlePathSelect(path)}
            />
          ))}
        </div>
        
        {/* Preview & Generate Sheet */}
        <PathPreviewSheet
          open={previewSheetOpen}
          onOpenChange={setPreviewSheetOpen}
          selectedPath={selectedPath}
          previewMeals={previewMeals}
          loadingPreview={loadingPreview}
          generating={generating}
          onGenerate={handleGenerate}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="path-selector">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-xl md:text-2xl font-semibold font-['Playfair_Display'] mb-2">
          Choose Your Week
        </h2>
        <p className="text-sm text-muted-foreground">
          Pick a style, we&apos;ll plan the meals
        </p>
      </div>

      {/* Path Grid - 2 cols mobile, 4 cols desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {paths.map((path) => (
          <PathCard
            key={path.id}
            path={path}
            onSelect={() => handlePathSelect(path)}
          />
        ))}
      </div>

      {/* Preview & Generate Sheet */}
      <PathPreviewSheet
        open={previewSheetOpen}
        onOpenChange={setPreviewSheetOpen}
        selectedPath={selectedPath}
        previewMeals={previewMeals}
        loadingPreview={loadingPreview}
        generating={generating}
        onGenerate={handleGenerate}
      />
    </div>
  );
};

/**
 * Path Preview Bottom Sheet
 */
const PathPreviewSheet = ({ 
  open, 
  onOpenChange, 
  selectedPath, 
  previewMeals, 
  loadingPreview, 
  generating, 
  onGenerate 
}) => {
  if (!selectedPath) return null;
  
  const rules = selectedPath.rules || {};
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl pb-8 max-h-[90vh]" data-testid="path-preview-sheet">
        <ScrollArea className="max-h-[calc(90vh-2rem)]">
          <SheetHeader className="text-left pb-4">
            <div className="flex items-center gap-3">
              <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${selectedPath.gradient || 'from-primary to-primary/80'} flex items-center justify-center`}>
                <span className="text-2xl">{selectedPath.emoji}</span>
              </div>
              <div className="flex-1">
                <SheetTitle className="text-xl font-['Playfair_Display']">
                  {selectedPath.name}
                </SheetTitle>
                <SheetDescription className="text-sm">
                  {selectedPath.subtitle}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Path Description */}
          <p className="text-sm text-muted-foreground mb-4">
            {selectedPath.description}
          </p>

          {/* Rules/Filters Pills */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="outline" className="text-xs py-1">
              <Calendar className="h-3 w-3 mr-1" />
              {selectedPath.mealsPerWeek || 5} meals
            </Badge>
            {rules.time?.label && (
              <Badge variant="outline" className="text-xs py-1">
                <Clock className="h-3 w-3 mr-1" />
                {rules.time.label}
              </Badge>
            )}
            {rules.servings?.label && (
              <Badge variant="outline" className="text-xs py-1">
                <Users className="h-3 w-3 mr-1" />
                {rules.servings.label}
              </Badge>
            )}
            {rules.budget?.label && (
              <Badge variant="outline" className="text-xs py-1">
                <DollarSign className="h-3 w-3 mr-1" />
                {rules.budget.label}
              </Badge>
            )}
            {rules.diet?.label && rules.diet.type && (
              <Badge variant="outline" className="text-xs py-1 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                <Leaf className="h-3 w-3 mr-1 text-green-600" />
                {rules.diet.label}
              </Badge>
            )}
            {rules.calories?.label && rules.calories.max && (
              <Badge variant="outline" className="text-xs py-1">
                {rules.calories.label}
              </Badge>
            )}
            {rules.protein?.label && (
              <Badge variant="outline" className="text-xs py-1 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
                <Dumbbell className="h-3 w-3 mr-1 text-red-600" />
                {rules.protein.label}
              </Badge>
            )}
          </div>

          {/* Ideal For */}
          {selectedPath.idealFor && selectedPath.idealFor.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">IDEAL FOR</p>
              <div className="flex flex-wrap gap-1.5">
                {selectedPath.idealFor.map((item, idx) => (
                  <span key={idx} className="text-xs bg-muted px-2 py-1 rounded-full">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Preview Meals */}
          <div className="mb-6">
            <p className="text-sm font-medium mb-3">Sample Meals</p>
            {loadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : previewMeals.length > 0 ? (
              <div className="space-y-2">
                {previewMeals.map((meal, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 p-2 rounded-lg bg-muted/50"
                  >
                    <img 
                      src={meal.recipe_image} 
                      alt={meal.recipe_title}
                      className="h-12 w-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{meal.recipe_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {meal.ready_in_minutes} min
                      </p>
                    </div>
                    <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground text-center pt-2">
                  + {(selectedPath.mealsPerWeek || 5) - previewMeals.length} more meals will be added
                </p>
              </div>
            ) : (
              <div className="text-center py-6 bg-muted/30 rounded-lg">
                <Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Tap generate to see your personalized meals
                </p>
              </div>
            )}
          </div>

          {/* Generate Button */}
          <Button
            className={`w-full h-14 text-base bg-gradient-to-r ${selectedPath.gradient || 'from-primary to-primary'} hover:opacity-90 transition-opacity`}
            onClick={onGenerate}
            disabled={generating}
            data-testid="generate-plan-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Generating Your Week...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                Generate My Week
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center mt-3">
            You can always swap or customize meals later
          </p>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Individual Path Card Component - Full version
 */
const PathCard = ({ path, onSelect }) => {
  const IconComponent = pathIcons[path.icon] || Sparkles;
  
  return (
    <Card 
      className={`cursor-pointer hover:border-primary/50 hover:shadow-md transition-all active:scale-[0.98] overflow-hidden`}
      onClick={onSelect}
      data-testid={`path-${path.id}`}
    >
      <CardContent className="p-4">
        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${path.gradient || 'from-primary to-primary/80'} flex items-center justify-center mb-3`}>
          <span className="text-xl">{path.emoji}</span>
        </div>
        <h3 className="font-semibold text-sm mb-0.5 line-clamp-1">{path.name}</h3>
        <p className="text-xs text-muted-foreground mb-2">{path.subtitle}</p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{path.rules?.time?.maxMinutes || path.filters?.maxReadyTime || 45} min</span>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Compact Path Card for horizontal scroll
 */
const CompactPathCard = ({ path, onSelect }) => {
  return (
    <div 
      className={`flex-shrink-0 w-32 cursor-pointer group`}
      onClick={onSelect}
      data-testid={`path-compact-${path.id}`}
    >
      <div className={`h-24 w-full rounded-xl bg-gradient-to-br ${path.gradient || 'from-primary to-primary/80'} flex items-center justify-center mb-2 group-hover:scale-[1.02] transition-transform shadow-sm`}>
        <span className="text-3xl">{path.emoji}</span>
      </div>
      <p className="text-xs font-medium text-center line-clamp-1">{path.name}</p>
      <p className="text-[10px] text-muted-foreground text-center">{path.subtitle}</p>
    </div>
  );
};

export default PathSelector;
